import React from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Channel } from '@meradio/shared-types';
import { Input, Select, Button, IconButton, Tooltip } from '@/components/ui';
import { FilterOptions } from './useVideoFilters';

export interface VideoFilterBarProps {
  filters: FilterOptions;
  setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
  resetFilters: () => void;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  channels: Channel[];
  sortOptions?: Array<{ value: string; label: string }>;
  totalCount?: number;
  children?: React.ReactNode;
}

const keyNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const VideoFilterBar: React.FC<VideoFilterBarProps> = ({
  filters,
  setFilters,
  resetFilters,
  sortBy,
  setSortBy,
  showFilters,
  setShowFilters,
  channels,
  sortOptions,
  totalCount,
  children,
}) => {
  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== '' && v !== null && (typeof v !== 'object' || v.min !== 60 || v.max !== 200)
  );

  return (
    <div className="mx-4 md:mx-0 mb-4 md:mb-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Input
            type="text"
            placeholder="Search videos, artists, or descriptions..."
            value={filters.searchQuery}
            onChange={(e) => setFilters((prev) => ({ ...prev, searchQuery: e.target.value }))}
            leftIcon={<Search className="w-5 h-5" />}
          />
          {filters.searchQuery && (
            <IconButton
              aria-label="Clear search"
              size="sm"
              variant="ghost"
              onClick={() => setFilters((prev) => ({ ...prev, searchQuery: '' }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </IconButton>
          )}
        </div>

        <div className="flex items-center gap-4">
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="recent">Most Recent</option>
            <option value="oldest">Oldest</option>
            <option value="title">Title A-Z</option>
            {sortOptions?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Tooltip label="Filters">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filters"
              className={`px-3.5 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-meradio-primary ${
                showFilters || hasActiveFilters
                  ? 'bg-meradio-primary border-meradio-primary text-white'
                  : 'border-neutral-300 dark:border-border-dark bg-white dark:bg-bg-dark-tertiary text-meradio-dark dark:text-text-dark-primary'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
          </Tooltip>
        </div>
      </div>

      {showFilters && (
        <div className="mt-4 bg-white dark:bg-bg-dark-secondary rounded-xl border border-neutral-200 dark:border-border-dark p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {channels.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Channel</label>
              <Select
                className="w-full"
                value={filters.channelId}
                onChange={(e) => setFilters((prev) => ({ ...prev, channelId: e.target.value }))}
              >
                <option value="">All Channels</option>
                {channels.map((channel) => (
                  <option key={channel.channelId} value={channel.channelId}>
                    {channel.name}
                  </option>
                ))}
              </Select>
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 whitespace-nowrap">
                BPM Range ({filters.bpmRange.min} - {filters.bpmRange.max})
              </label>
              <div className="relative h-[50px] flex items-center">
                <div className="relative w-full h-1.5 rounded-full bg-neutral-300 dark:bg-bg-dark-primary">
                  <div
                    className="absolute h-full rounded-full bg-meradio-primary"
                    style={{
                      left: `${((filters.bpmRange.min - 60) / 140) * 100}%`,
                      right: `${100 - ((filters.bpmRange.max - 60) / 140) * 100}%`,
                    }}
                  />
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={filters.bpmRange.min}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        bpmRange: {
                          ...prev.bpmRange,
                          min: Math.min(parseInt(e.target.value), prev.bpmRange.max),
                        },
                      }))
                    }
                    className="dual-range"
                    aria-label="Minimum BPM"
                  />
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={filters.bpmRange.max}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        bpmRange: {
                          ...prev.bpmRange,
                          max: Math.max(parseInt(e.target.value), prev.bpmRange.min),
                        },
                      }))
                    }
                    className="dual-range"
                    aria-label="Maximum BPM"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Musical Key</label>
              <Select
                className="w-full"
                value={filters.keySignature || ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    keySignature: e.target.value ? parseInt(e.target.value) : null,
                  }))
                }
              >
                <option value="">Any Key</option>
                {keyNames.map((key, index) => (
                  <option key={index} value={index}>
                    {key}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Mode</label>
              <Select
                className="w-full"
                value={filters.mode !== null ? filters.mode.toString() : ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    mode: e.target.value ? parseInt(e.target.value) : null,
                  }))
                }
              >
                <option value="">Any Mode</option>
                <option value="0">Minor</option>
                <option value="1">Major</option>
              </Select>
            </div>
          </div>

          {children}

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="link"
              size="sm"
              onClick={resetFilters}
              className="text-neutral-600 hover:text-neutral-900"
            >
              Reset all filters
            </Button>
            {typeof totalCount === 'number' && (
              <div className="text-sm text-neutral-600">
                Filtered from {totalCount} total videos
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoFilterBar;
